import { CalendarCheck, CalendarOff, Clock, PlugZap, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { usePersonPto } from "@/hooks/use-pto";
import { type LeaveTypeTally, leaveTypeBreakdown, type PersonLeaveItem } from "@/lib/person-leave";
import {
  formatDays,
  formatHours,
  formatPtoDay,
  PTO_TYPE_LABELS,
  PTO_TYPE_TONE,
  type PtoType,
} from "@/lib/pto";
import { BUCKET_LABELS, formatDayCount, type PtoBucketSummary } from "@/lib/pto-balance";
import { cn } from "@/lib/utils";

/**
 * Upcoming leave for one person: approved bookings and requests still awaiting
 * approval, in one dated list.
 *
 * Restricted to the person, their manager and admins — sick leave is in here.
 * The profile only mounts this for those viewers; the API enforces it too.
 */
export function PersonPtoSection({
  personId,
  personName,
}: {
  personId: string;
  personName: string;
}) {
  const { data, isLoading, isError, error, refetch } = usePersonPto(personId, true);
  const [hidden, setHidden] = useState<Set<PtoType>>(new Set());

  // Chips come from the UNFILTERED list; hiding a type must not remove its own
  // chip, or there would be no way to turn it back on.
  const allItems = data?.items ?? [];
  const tallies = useMemo(() => leaveTypeBreakdown(allItems), [allItems]);
  const items = useMemo(() => allItems.filter((i) => !hidden.has(i.type)), [allItems, hidden]);

  function toggleType(type: PtoType) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  if (isLoading) {
    return (
      <DetailSection title="Upcoming time off">
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-3/4" />
        </div>
      </DetailSection>
    );
  }

  if (isError) {
    return (
      <DetailSection title="Upcoming time off">
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load this person's leave"}
          onRetry={() => {
            void refetch();
          }}
        />
      </DetailSection>
    );
  }

  if (data && !data.connected) {
    return (
      <DetailSection title="Upcoming time off">
        <EmptyState
          icon={PlugZap}
          title="Not connected to NOS"
          description="Leave is read live from NOS, and this environment has no gateway credentials. Nothing is missing from Enable — there is just nothing to read."
        />
      </DetailSection>
    );
  }

  return (
    <DetailSection title="Upcoming time off">
      {data?.pendingUnavailable && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-bg px-3 py-2 text-sm text-warning-foreground">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Approved leave loaded, but requests awaiting approval could not be read. Anything
            pending is missing from this list.
          </span>
        </div>
      )}

      {/* Absent for a colleague: the year's balances stay with the person, their
          direct manager and admins, and the server withholds them rather than
          the UI hiding them. `canViewBalances` is what keeps that distinct from
          "NOS recorded no allowance", which renders quite differently. */}
      {data?.canViewBalances !== false && (
        <BalanceSummary summary={data?.summary ?? []} year={data?.summaryYear ?? 0} />
      )}

      <TypeFilter
        tallies={tallies}
        hidden={hidden}
        onToggle={toggleType}
        onReset={() => setHidden(new Set())}
      />

      {items.length === 0 ? (
        <EmptyState
          icon={CalendarOff}
          title={hidden.size > 0 ? "Nothing left to show" : "No upcoming time off"}
          description={
            hidden.size > 0
              ? "Every leave type above is filtered out. Turn one back on to see it."
              : data?.canViewBalances === false
                ? // A colleague is not shown requests awaiting approval, so the
                  // empty state must not claim there are none.
                  `${personName} has no approved time off from today onward.`
                : `${personName} has nothing booked or requested from today onward.`
          }
        />
      ) : (
        <ul className="divide-y">
          {items.map((item) => (
            <LeaveRow key={item.key} item={item} showStatus={data?.canViewBalances !== false} />
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Read live from NOS.{" "}
        {data?.canViewBalances === false ? (
          <>
            This shows approved time off only — one line per day from today onward, twelve months
            ahead.
          </>
        ) : (
          <>
            The balances cover {data?.summaryYear ?? "this year"}; the list is one line per day from
            today onward, twelve months ahead. A request awaiting approval stays one line for its
            whole range with an estimated length — NOS only works out which days it covers once it
            is approved.
          </>
        )}
      </p>
    </DetailSection>
  );
}

/**
 * Allowance, accrual, used and what is left — per bucket, for the calendar year.
 *
 * Covers the whole year even though the list below is upcoming-only: a balance
 * that ignored the months already spent would be meaningless.
 */
function BalanceSummary({ summary, year }: { summary: PtoBucketSummary[]; year: number }) {
  if (summary.length === 0) return null;
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-2">
      {summary.map((bucket) => (
        <BucketCard key={bucket.key} bucket={bucket} year={year} />
      ))}
    </div>
  );
}

function BucketCard({ bucket, year }: { bucket: PtoBucketSummary; year: number }) {
  const allowance = bucket.allowanceDays;

  if (allowance === null) {
    return (
      <div className="rounded-xl border bg-muted/30 px-4 py-3.5">
        <BucketHeader label={BUCKET_LABELS[bucket.key]} year={year} allowance={null} />
        {/* Never a zero here. NOS records no allowance for roughly half of
            Enable's people, and "0 days left" is the one reading that must not
            happen to them. */}
        <p className="mt-2 text-muted-foreground text-sm">
          No allowance recorded in NOS.
          {bucket.usedDays > 0 && <> {formatDayCount(bucket.usedDays)} d taken so far.</>}
        </p>
      </div>
    );
  }

  const remaining = bucket.remainingDays ?? 0;
  const over = remaining < 0;

  return (
    <div className="rounded-xl border bg-muted/30 px-4 py-3.5">
      <BucketHeader label={BUCKET_LABELS[bucket.key]} year={year} allowance={allowance} />

      {/* The balance is the one number anyone opens this tab for, so it is the
          only thing at display size. Everything else is context for it. */}
      <div className="mt-2 flex items-end gap-2">
        <span
          className={cn(
            "font-semibold text-3xl leading-none tabular-nums",
            over && "text-danger-foreground",
          )}
        >
          {formatDayCount(remaining)}
          <span className="ml-1 font-normal text-lg text-muted-foreground">d</span>
        </span>
        <span className="pb-0.5 text-muted-foreground text-sm">
          {over ? "over the allowance" : "left this year"}
        </span>
      </div>

      <UsageBar used={bucket.usedDays} allowance={allowance} tone={BUCKET_BAR[bucket.key]} />

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs">
        <span>
          <strong className="font-semibold text-foreground tabular-nums">
            {formatDayCount(bucket.usedDays)} d
          </strong>{" "}
          used
        </span>
        {/* Shown only where accrual is real. Sick is one annual grant, so an
            "earned to date" figure there would repeat the allowance and imply a
            distinction that does not exist. */}
        {bucket.accruedDays !== null && (
          <SimpleTooltip
            content={
              bucket.accruedLabel === null
                ? "Earned so far this year."
                : `Counts the quarters that have started: ${bucket.accruedLabel}. PTO accrues as a lump each quarter, so this steps up on 1 Jan, 1 Apr, 1 Jul and 1 Oct.`
            }
          >
            <span className="underline decoration-dotted underline-offset-2">
              <strong className="font-semibold text-foreground tabular-nums">
                {formatDayCount(bucket.accruedDays)} d
              </strong>{" "}
              earned to date
            </span>
          </SimpleTooltip>
        )}
        {bucket.pendingDays > 0 && (
          <SimpleTooltip content="Requests still awaiting approval. Deliberately NOT deducted from the balance above — nothing unapproved moves it.">
            <span className="inline-flex items-center gap-1 rounded bg-warning-bg px-1.5 py-0.5 font-medium text-warning-foreground">
              <Clock className="size-3" aria-hidden />
              {formatDayCount(bucket.pendingDays)} d awaiting approval
            </span>
          </SimpleTooltip>
        )}
      </div>
    </div>
  );
}

/** Per-bucket bar colour, echoing each type's chip tint. */
const BUCKET_BAR: Record<PtoBucketSummary["key"], string> = {
  pto: "bg-info",
  sick: "bg-danger",
};

function BucketHeader({
  label,
  year,
  allowance,
}: {
  label: string;
  year: number;
  allowance: number | null;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <h3 className="font-semibold text-sm">{label}</h3>
      <span className="text-muted-foreground text-xs">
        {allowance === null ? year : `of ${formatDayCount(allowance)} d · ${year}`}
      </span>
    </div>
  );
}

/**
 * How much of the allowance is spent, as a bar.
 *
 * The proportion is the thing a column of numbers does not show at a glance.
 * Width is clamped at 100% so an over-booked balance fills the track rather than
 * overflowing it — the negative number above says by how much.
 */
function UsageBar({ used, allowance, tone }: { used: number; allowance: number; tone: string }) {
  if (allowance <= 0) return null;
  const percent = Math.min(100, Math.max(0, (used / allowance) * 100));
  return (
    <div
      className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-border"
      role="img"
      aria-label={`${formatDayCount(used)} of ${formatDayCount(allowance)} days used`}
    >
      <div className={cn("h-full rounded-full", tone)} style={{ width: `${percent}%` }} />
    </div>
  );
}

/**
 * Type filter for the list below it — not for the tiles, which are per-bucket by
 * definition. Sits directly above the list so its scope is obvious.
 *
 * Only types the person actually has, so nobody gets three dead chips. Derived
 * from the UNFILTERED list, or a chip would disappear the moment it was used.
 */
function TypeFilter({
  tallies,
  hidden,
  onToggle,
  onReset,
}: {
  tallies: LeaveTypeTally[];
  hidden: Set<PtoType>;
  onToggle: (type: PtoType) => void;
  onReset: () => void;
}) {
  // Rendered even for a single type: the chip still labels the whole list and
  // totals it, and hiding the control on the (common) single-type profile is
  // exactly where someone looks for it first.
  if (tallies.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5">
      {tallies.map(({ type, days, lines }) => {
        const off = hidden.has(type);
        return (
          <button
            key={type}
            type="button"
            onClick={() => onToggle(type)}
            aria-pressed={!off}
            title={`${lines} ${lines === 1 ? "line" : "lines"} · ${formatDayCount(days)} d`}
            className={cn(
              "rounded-full px-2.5 py-1 text-xs transition-colors",
              off
                ? "bg-muted text-muted-foreground opacity-60 hover:opacity-100"
                : PTO_TYPE_TONE[type],
            )}
          >
            {PTO_TYPE_LABELS[type]}{" "}
            <span className="tabular-nums opacity-70">{formatDayCount(days)} d</span>
          </button>
        );
      })}
      {hidden.size > 0 && (
        <button
          type="button"
          onClick={onReset}
          className="ml-1 text-muted-foreground text-xs underline underline-offset-2 hover:text-foreground"
        >
          Show all
        </button>
      )}
    </div>
  );
}

/**
 * One absence: dates, type, how long, and — where it can vary — its status.
 *
 * `showStatus` is false for a viewer who only ever receives approved leave (a
 * colleague). "Approved" on every row of a list that cannot contain anything
 * else is noise, and it invites the reader to wonder what the other rows are.
 * The person, their manager and admins do see pending leave, so for them the
 * status is load-bearing and stays.
 */
function LeaveRow({ item, showStatus }: { item: PersonLeaveItem; showStatus: boolean }) {
  const single = item.startDate === item.endDate;
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
      <span className="min-w-[11rem] font-medium">
        {formatPtoDay(item.startDate)}
        {!single && <> – {formatPtoDay(item.endDate)}</>}
      </span>

      <span className={cn("rounded px-1.5 py-0.5 text-xs", PTO_TYPE_TONE[item.type])}>
        {PTO_TYPE_LABELS[item.type]}
      </span>

      {showStatus && <StatusChip status={item.status} approverName={item.approverName} />}

      <SimpleTooltip
        content={
          item.estimated
            ? `About ${formatHours(item.hours)} — estimated from the request's dates, so a company holiday inside it would make this read high.`
            : formatHours(item.hours)
        }
      >
        <span className="text-sm text-muted-foreground tabular-nums">
          {item.estimated && "~"}
          {formatDays(item.hours)}d
        </span>
      </SimpleTooltip>

      {item.description !== null && (
        <span className="w-full text-sm text-muted-foreground sm:w-auto sm:flex-1">
          {item.description}
        </span>
      )}
    </li>
  );
}

/**
 * Approved vs awaiting approval.
 *
 * Pending is the one that needs saying, so it carries the icon and the
 * approver's name; approved is the quiet default. Never rendered as a bare
 * colour — the status is the whole point of the column.
 */
function StatusChip({
  status,
  approverName,
}: {
  status: PersonLeaveItem["status"];
  approverName: string | null;
}) {
  if (status === "approved") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <CalendarCheck className="size-3.5" aria-hidden />
        Approved
      </span>
    );
  }
  return (
    <SimpleTooltip
      content={
        approverName === null
          ? "Submitted, with no approver assigned yet."
          : `Waiting on ${approverName}.`
      }
    >
      <span className="inline-flex items-center gap-1 rounded bg-warning-bg px-1.5 py-0.5 text-xs font-medium text-warning-foreground">
        <Clock className="size-3.5" aria-hidden />
        Awaiting approval
      </span>
    </SimpleTooltip>
  );
}
